import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Skeleton from '../../../atoms/Skeleton'
import RoundedRectangleNode from '../Canvas/nodes/RoundedRectangleNode'
import SydInput from '../../../commonDesign/SydInput'
import { ICON_NAMES } from '../../../../constants'
import SydLabel from '../../../commonDesign/SydLabel'
import { useAppContext } from '../../../../redux/slices/appContext'
import { useEntityGraphContext } from '../Wrapper/EntityGraphProvider'
import { useLevelThings } from '../../../../api/things'
import { getRandomId } from './utils'

const getThingData = (thing) => ({
  header: thing.name,
  description: thing.content,
  nodeProps: {
    size: 'sm'
  },
  entityProps: {
    levelTypeId: 200,
    levelId: thing.thingId,
    type: 'thing',
    data: thing
  },
  displayOptions: {
    header: 'entityProps.label',
    description: 'entityProps.description',
    image: 'entityProps.image'
  }
})

export const ThingsList = ({
  actionType = 'creation',
  onSelectItem: _onSelectItem,
  selectedValue
}) => {
  const { clientId } = useAppContext()
  const { dispatch } = useEntityGraphContext()
  const [searchTerm, setSearchTerm] = useState('')

  const addLinkedDataToContext = useCallback((thing) => {
    dispatch({
      type: 'ADD_LINKED_ENTITY_DATA',
      payload: {
        levelTypeId: 200,
        levelId: thing.thingId,
        value: {
          label: `${thing.name}`,
          description: thing.content,
          ...thing
        }
      }
    })
  }, [dispatch])

  const onSelectItem = useCallback((thing) => {
    addLinkedDataToContext(thing)
    _onSelectItem?.(getThingData(thing))
  }, [addLinkedDataToContext, _onSelectItem])

  const { data, isLoading } = useLevelThings(201, clientId)

  const filteredThings = useMemo(() => {
    if (!searchTerm?.length) {
      return data?.things ?? []
    }
    return data.things.filter(thing => thing.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [data, searchTerm])

  const onDragStart = (event, account) => {
    addLinkedDataToContext(account)
    event.dataTransfer.setData('application/reactflow', JSON.stringify({
      nodeType: 'roundedRectangle',
      id: `account_${account.accountId}_${getRandomId()}`,
      data: getThingData(account)
    }))
    event.dataTransfer.effectAllowed = 'move'
  }

  if (selectedValue) {
    return (
      <>
        {isLoading && (
          <Skeleton width='100%' height='38px' />
        )}
        {data.data?.filter(thing => thing.thingId === selectedValue.levelId)?.map((thing) => (
          <RoundedRectangleNode
            key={thing.thingId}
            width='100%'
            height='auto'
            data={{
              ...getThingData(thing),
              displayOptions: {
                label: 'entityProps.name',
                description: 'entityProps.content'
              },
              nodeProps: {
                size: 'md',
                handles: [],
                targets: []
              }
            }}
          />
        ))}
      </>
    )
  }

  return (
    <>
      <div>
        <SydInput
          size='sm'
          iconName={ICON_NAMES.search}
          placeholder='Search things...'
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          className='mb-2'
        />
      </div>
      <div className='flex flex-col gap-sm'>
        <SydLabel label={actionType === 'linking' ? 'Click a thing to link them' : 'Drag thing into the chart'} />
        {isLoading && Array.from({ length: 10 }).map((_, index) => (
          <Skeleton width='100%' height='38px' key={index} />
        ))}
        <div className='flex-1 flex-col gap-2 flex' style={{ overflowY: 'auto', maxHeight: '500px' }}>
          {filteredThings?.map((thing) => (
            <div
              draggable={actionType === 'creation'}
              key={thing.thingId}
              className='relative'
              style={{
                cursor: actionType === 'creation' ? 'grab' : 'pointer'
              }}
              onDragStart={(e) => onDragStart(e, thing)}
              onClick={() => {
                if (actionType === 'linking' && onSelectItem) {
                  onSelectItem(thing)
                }
              }}
            >
              <RoundedRectangleNode
                width='100%' height='auto' data={{
                  ...getThingData(thing),
                  displayOptions: {},
                  nodeProps: {
                    size: 'md',
                    handles: [],
                    targets: []
                  }
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

ThingsList.propTypes = {
  actionType: PropTypes.oneOf(['creation', 'linking']),
  onSelectItem: PropTypes.func,
  selectedValue: PropTypes.shape({
    levelId: PropTypes.string,
    type: PropTypes.string
  })
}
